import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet } from '../../lib/api';
import { Wallet, TrendingUp, TrendingDown, ArrowDownCircle, ArrowUpCircle, ArrowRight, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { formatINR } from '../../lib/format';

const KPI = ({ icon: Icon, label, value, sub, tone = 'default', testId }) => {
  const tones = { default: 'bg-white border-slate-200', amber: 'bg-amber-50 border-amber-200', red: 'bg-red-50 border-red-200', green: 'bg-emerald-50 border-emerald-200' };
  return (
    <div className={`rounded-sm border p-4 ${tones[tone]}`} data-testid={testId}>
      <div className="flex items-center gap-2 text-[10px] font-display font-bold uppercase tracking-[0.16em] text-slate-500"><Icon size={13} /> {label}</div>
      <div className="text-xl font-display font-extrabold mt-2 text-[#3b4872]">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
};

export default function FinancialOverview() {
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => { apiGet('/finance/company-overview').then(setD).catch(() => setD({})); }, []);
  if (!d) return <div className="p-8 text-slate-500" data-testid="overview-loading">Loading…</div>;

  return (
    <div className="space-y-5 p-6 md:p-8" data-testid="financial-overview">
      <div>
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">Finance & Accounts</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Financial Overview</h1>
        <p className="text-sm text-slate-600 mt-1">Company-wide snapshot · FY {d.fy?.start?.slice(0, 4)}–{d.fy?.end?.slice(2, 4)} · Tally remains the book of record.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={ArrowUpCircle} label="Revenue (FY)" value={formatINR(d.revenue_raised)} sub="RA bills raised" tone="green" testId="kpi-revenue" />
        <KPI icon={ArrowDownCircle} label="Costs (FY)" value={formatINR(d.total_cost)} sub="Proc + payroll + petty + fuel" testId="kpi-cost" />
        <KPI icon={d.gross_margin >= 0 ? TrendingUp : TrendingDown} label="Gross Margin" value={formatINR(d.gross_margin)} sub={d.gross_margin_pct != null ? `${d.gross_margin_pct}%` : '—'} tone={d.gross_margin >= 0 ? 'green' : 'red'} testId="kpi-margin" />
        <KPI icon={Wallet} label="Receivables" value={formatINR(d.receivables_open)} sub={`Payables ${formatINR(d.payables_open)}`} tone={d.receivables_open ? 'amber' : 'default'} testId="kpi-receivables" />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card data-testid="cost-breakdown-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-slate-500">Cost Breakdown (FY)</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(d.cost_breakdown || {}).map(([k, v]) => {
                const max = Math.max(1, ...Object.values(d.cost_breakdown || {}));
                return (
                  <div key={k} className="flex items-center gap-3" data-testid={`cost-${k}`}>
                    <div className="w-24 text-[11px] font-display font-bold text-slate-600 capitalize">{k.replace('_', ' ')}</div>
                    <div className="flex-1 bg-slate-100 rounded-sm h-5 overflow-hidden"><div className="h-full bg-[#3b4872]" style={{ width: `${(v / max) * 100}%` }} /></div>
                    <div className="w-24 text-right text-xs font-display font-bold">{formatINR(v)}</div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card data-testid="receivables-aging-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-slate-500">Receivables Aging</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-2">
              {Object.entries(d.receivables_aging || {}).map(([bucket, v]) => (
                <div key={bucket} className={`rounded-sm border p-2 text-center ${bucket === '90+' && v > 0 ? 'border-red-200 bg-red-50' : 'border-slate-200'}`} data-testid={`aging-${bucket}`}>
                  <div className="text-[10px] text-slate-500 font-bold">{bucket} days</div>
                  <div className="text-xs font-display font-bold mt-1">{formatINR(v)}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card data-testid="top-revenue-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-slate-500">Top Projects by Revenue</CardTitle></CardHeader>
          <CardContent>
            {(d.top_projects_by_revenue || []).length === 0 ? <div className="text-xs text-slate-400 py-4 text-center">No revenue recorded.</div> : (
              <div className="divide-y divide-slate-100">
                {d.top_projects_by_revenue.map((p, i) => (
                  <div key={i} className="py-2 flex items-center justify-between text-xs" data-testid={`toprev-${i}`}><span className="font-display font-bold">{p.project}</span><span className="text-emerald-700 font-bold">{formatINR(p.revenue)}</span></div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-red-400" data-testid="top-overrun-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-red-600 flex items-center gap-2"><AlertTriangle size={15} /> Top Projects by Cost Overrun</CardTitle></CardHeader>
          <CardContent>
            {(d.top_projects_by_overrun || []).length === 0 ? <div className="text-xs text-slate-400 py-4 text-center">No projects over budget. ✓</div> : (
              <div className="divide-y divide-slate-100">
                {d.top_projects_by_overrun.map((p, i) => (
                  <button key={i} onClick={() => nav('/app/finance/project-pnl')} className="w-full py-2 flex items-center justify-between text-xs hover:bg-slate-50 px-2 -mx-2 rounded-sm" data-testid={`overrun-${i}`}>
                    <span className="font-display font-bold">{p.project}</span>
                    <span className="text-red-600 font-bold flex items-center gap-1">+{formatINR(p.overrun)} <ArrowRight size={12} /></span>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
